import React, { useState, useEffect } from 'react';
import './CounterpartyMasterPage.css';
import * as XLSX from 'xlsx';
import { API_BASE_URL, getAuthHeaders } from '../services/api';

const joinAddress = (parts) => parts.map((p) => String(p ?? '').trim()).filter(Boolean).join(', ');

const personRow = (cp) => ({
  'CUX Number': cp.cux_number || '',
  'Title': cp.title || '',
  'Short Name': cp.short_name || '',
  'Long Name': cp.long_name || '',
  'ID Type': cp.id_type || '',
  'ID Number': cp.id_number || '',
  'Address': joinAddress([cp.house_number, cp.street_name, cp.city, cp.province, cp.postal_code, cp.country]),
  'House No': cp.house_number || '',
  'Street': cp.street_name || '',
  'City': cp.city || '',
  'Province': cp.province || '',
  'Postal Code': cp.postal_code || '',
  'Country': cp.country || '',
  'Telephone': cp.telephone || '',
  'Mobile': cp.mobile || '',
  'Email': cp.email || '',
  'Custodian Bank': cp.custodian_bank || '',
  'CDS Account': cp.cds_account || ''
});

const corporateRow = (cp) => ({
  'CUX Number': cp.cux_number || '',
  'Company Name': cp.company_name || '',
  'Short Name': cp.short_name || '',
  'Long Name': cp.long_name || '',
  'Registration Number': cp.registration_number || '',
  'TIN Number': cp.tin_number || '',
  'VAT Number': cp.vat_number || '',
  'Address': joinAddress([cp.address_line1, cp.address_line2, cp.city, cp.state, cp.postal_code, cp.country]),
  'Address Line 1': cp.address_line1 || '',
  'Address Line 2': cp.address_line2 || '',
  'City': cp.city || '',
  'State': cp.state || '',
  'Postal Code': cp.postal_code || '',
  'Country': cp.country || '',
  'Phone': cp.phone_number || '',
  'Email': cp.email || '',
  'Website': cp.website || '',
  'Custodian Bank': cp.custodian_bank || '',
  'CDS Account': cp.cds_account || ''
});

const exportCounterpartiesToExcel = (lists) => {
  const wb = XLSX.utils.book_new();
  const addSheet = (name, rows) => {
    const ws = XLSX.utils.json_to_sheet(rows);
    const headers = rows.length ? Object.keys(rows[0]) : [];
    ws['!cols'] = headers.map((h) => ({
      wch: Math.min(60, Math.max(h.length, ...rows.map((r) => String(r[h] ?? '').length)) + 2)
    }));
    XLSX.utils.book_append_sheet(wb, ws, name);
  };
  addSheet('Individual', (lists.individual || []).map(personRow));
  addSheet('Joint', (lists.joint || []).map(personRow));
  addSheet('Corporate', (lists.corporate || []).map(corporateRow));
  XLSX.writeFile(wb, `counterparty_master_${new Date().toISOString().slice(0, 10)}.xlsx`);
};

const individualTemplate = {
  title: 'Mr',
  shortName: '',
  longName: '',
  idType: '',
  idNumber: '',
  address: {
    houseNumber: '',
    streetName: '',
    province: '',
    postalCode: '',
    city: '',
    country: '',
    telephone: '',
    email: '',
    mobile: '',
  },
  custodian_bank: '',
  cds_account: '',
};

const jointTemplate = {
  title: 'Mr',
  shortName: '',
  longName: '',
  idType: 'NIC',
  address: {
    houseNumber: '',
    streetName: '',
    province: '',
    postalCode: '',
    city: '',
    country: '',
    telephone: '',
    email: '',
    mobile: '',
  },
  custodian_bank: '',
  cds_account: '',
};

const jointCounterpartyFormTemplate = {
  title: 'Mr',
  shortName: '',
  longName: '',
  idType: 'NIC',
  idNumber: '',
  address: {
    houseNumber: '',
    streetName: '',
    province: '',
    postalCode: '',
    city: '',
    country: '',
    telephone: '',
    email: '',
    mobile: '',
  },
  cds_account: '',
  custodian_bank: '',
};

const corporateTemplate = {
  companyName: '',
  shortName: '',
  longName: '',
  registrationNumber: '',
  tinNumber: '',
  vatNumber: '',
  address: {
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    phoneNumber: '',
    email: '',
    website: '',
  },
  kycStatus: 'Pending',
  riskCategory: 'Low',
  sanctionsCheck: 'Passed',
  creditLimit: '',
  banking: {
    primaryBankName: '',
    bankAccountNumber: '',
    swiftBicCode: '',
    treasuryContactPerson: '',
    treasuryContactEmail: '',
    treasuryContactPhone: '',
  },
  custodian_bank: '',
  cds_account: '',
};

const idTypes = ['NIC', 'Passport', 'Driving License', 'Business Registration', 'Other'];
const titles = ['Mr', 'Miss', 'Mrs', 'Dr', 'Rev'];
const kycStatuses = ['Pending', 'Verified', 'Rejected'];
const riskCategories = ['Low', 'Medium', 'High'];
const sanctionsChecks = ['Passed', 'Failed'];

// Static list of custodian banks
const CUSTODIAN_BANKS = [
  "Bank of Ceylon",
  "People's Bank",
  "Commercial Bank",
  "Sampath Bank",
  "Hatton National Bank",
  "Seylan Bank",
  "NDB Bank",
  "DFCC Bank"
];

export default function CounterpartyMasterPage() {
  const [type, setType] = useState('Individual');
  const [individual, setIndividual] = useState({ ...individualTemplate });
  const [joint, setJoint] = useState({ ...jointTemplate });
  const [corporate, setCorporate] = useState({ ...corporateTemplate });
  const [showJointToast, setShowJointToast] = useState(false);
  const [showIndividualToast, setShowIndividualToast] = useState(false);
  const [showCorporateToast, setShowCorporateToast] = useState(false);
  const [savedCuxNumber, setSavedCuxNumber] = useState(null); // Store CUX number after save
  
  // New states for viewing/editing
  const [viewMode, setViewMode] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showList, setShowList] = useState(false);
  const [counterparties, setCounterparties] = useState({ individual: [], joint: [], corporate: [] });
  const [loading, setLoading] = useState(false);
  const [numberOfCounterparties, setNumberOfCounterparties] = useState(1);
  const [jointCounterparties, setJointCounterparties] = useState([{ ...jointCounterpartyFormTemplate }]);
  const [validationError, setValidationError] = useState('');

  // Fetch all counterparties
  const fetchCounterparties = async () => {
    setLoading(true);
    try {
      const [individualRes, jointRes, corporateRes] = await Promise.all([
        fetch(`${API_BASE_URL}/counterparty-individual`).then(r => r.json()),
        fetch(`${API_BASE_URL}/counterparty-joint`).then(r => r.json()),
        fetch(`${API_BASE_URL}/counterparty-corporate`).then(r => r.json())
      ]);
      setCounterparties({
        individual: individualRes || [],
        joint: jointRes || [],
        corporate: corporateRes || []
      });
    } catch (err) {
      console.error('Error fetching counterparties:', err);
      alert('Error fetching counterparties: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showList) {
      fetchCounterparties();
    }
  }, [showList, type]);


  const handleTypeChange = (e) => {
    const newType = e.target.value;
    setType(newType);
    // Reset modes when changing type
    setViewMode(false);
    setEditMode(false);
    setEditingId(null);
    // Reset forms
    setIndividual({ ...individualTemplate });
    setJoint({ ...jointTemplate });
    setCorporate({ ...corporateTemplate });
    // Reset joint counterparty forms
    setNumberOfCounterparties(1);
    setJointCounterparties([{ ...jointCounterpartyFormTemplate }]);
  };

  // Handle view action
  const handleView = async (id) => {
    setLoading(true);
    try {
      let url = '';
      if (type === 'Individual') url = `${API_BASE_URL}/counterparty-individual/${id}`;
      else if (type === 'Joint') url = `${API_BASE_URL}/counterparty-joint/${id}`;
      else url = `${API_BASE_URL}/counterparty-corporate/${id}`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch counterparty');
      const data = await response.json();
      
      // Populate form based on type
      if (type === 'Individual') {
        setIndividual({
          title: data.title || '',
          shortName: data.short_name || '',
          longName: data.long_name || '',
          idType: data.id_type || '',
          idNumber: data.id_number || '',
          address: {
            houseNumber: data.house_number || '',
            streetName: data.street_name || '',
            province: data.province || '',
            postalCode: data.postal_code || '',
            city: data.city || '',
            country: data.country || '',
            telephone: data.telephone || '',
            email: data.email || '',
            mobile: data.mobile || '',
          },
          custodian_bank: data.custodian_bank || '',
          cds_account: data.cds_account || '',
        });
        setSavedCuxNumber(data.cux_number || null);
      } else if (type === 'Joint') {
        setJoint({
          title: data.title || '',
          shortName: data.short_name || '',
          longName: data.long_name || '',
          idType: data.id_type || '',
          address: {
            houseNumber: data.house_number || '',
            streetName: data.street_name || '',
            province: data.province || '',
            postalCode: data.postal_code || '',
            city: data.city || '',
            country: data.country || '',
            telephone: data.telephone || '',
            email: data.email || '',
            mobile: data.mobile || '',
          },
          custodian_bank: data.custodian_bank || '',
          cds_account: data.cds_account || '',
        });
        setSavedCuxNumber(data.cux_number || null);
        
        // Set counterparty forms if available
        if (data.counterparties && Array.isArray(data.counterparties) && data.counterparties.length > 0) {
          setNumberOfCounterparties(data.counterparties.length);
          const forms = data.counterparties.map(cp => ({
            title: cp.title || '',
            shortName: cp.shortName || cp.short_name || '',
            longName: cp.longName || cp.long_name || '',
            idType: cp.idType || cp.id_type || '',
            idNumber: cp.idNumber || cp.id_number || '',
            address: {
              houseNumber: cp.address?.houseNumber || cp.address?.house_number || '',
              streetName: cp.address?.streetName || cp.address?.street_name || '',
              province: cp.address?.province || '',
              postalCode: cp.address?.postalCode || cp.address?.postal_code || '',
              city: cp.address?.city || '',
              country: cp.address?.country || '',
              telephone: cp.address?.telephone || '',
              email: cp.address?.email || '',
              mobile: cp.address?.mobile || ''
            },
            cds_account: cp.cds_account || '',
            custodian_bank: cp.custodian_bank || ''
          }));
          setJointCounterparties(forms);
        } else {
          setNumberOfCounterparties(1);
          setJointCounterparties([{ ...jointCounterpartyFormTemplate }]);
        }
      } else {
        setCorporate({
          companyName: data.company_name || '',
          shortName: data.short_name || '',
          longName: data.long_name || '',
          registrationNumber: data.registration_number || '',
          tinNumber: data.tin_number || '',
          vatNumber: data.vat_number || '',
          address: {
            addressLine1: data.address_line1 || '',
            addressLine2: data.address_line2 || '',
            city: data.city || '',
            state: data.state || '',
            country: data.country || '',
            postalCode: data.postal_code || '',
            phoneNumber: data.phone_number || '',
            email: data.email || '',
            website: data.website || '',
          },
          kycStatus: data.kyc_status || 'Pending',
          riskCategory: data.risk_category || 'Low',
          sanctionsCheck: data.sanctions_check || 'Passed',
          creditLimit: data.credit_limit || '',
          banking: {
            primaryBankName: data.primary_bank_name || '',
            bankAccountNumber: data.bank_account_number || '',
            swiftBicCode: data.swift_bic_code || '',
            treasuryContactPerson: data.treasury_contact_person || '',
            treasuryContactEmail: data.treasury_contact_email || '',
            treasuryContactPhone: data.treasury_contact_phone || '',
          },
          custodian_bank: data.custodian_bank || '',
          cds_account: data.cds_account || '',
        });
        setSavedCuxNumber(data.cux_number || null);
      }
      
      setViewMode(true);
      setEditMode(false);
      setShowList(false);
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle edit action
  const handleEdit = async (id) => {
    setEditingId(id);
    await handleView(id);
    setViewMode(false);
    setEditMode(true);
  };

  // Reset form
  const handleReset = () => {
    setViewMode(false);
    setEditMode(false);
    setEditingId(null);
    setIndividual({ ...individualTemplate });
    setJoint({ ...jointTemplate });
    setCorporate({ ...corporateTemplate });
    setSavedCuxNumber(null);
    setNumberOfCounterparties(1);
    setJointCounterparties([{ ...jointCounterpartyFormTemplate }]);
  };

  const handleIndividualChange = (field, value) => {
    // Clear validation error when user changes NIC number
    if (field === 'idNumber') {
      setValidationError('');
    }
    setIndividual(prev => {
      if (field in prev) {
        return { ...prev, [field]: value };
      } else if (field in prev.address) {
        return { ...prev, address: { ...prev.address, [field]: value } };
      }
      return prev;
    });
  };

  const handleIndividualExtraChange = (field, value) => {
    setIndividual(prev => ({ ...prev, [field]: value }));
  };

  const handleJointChange = (field, value) => {
    setJoint(prev => {
      if (field in prev) {
        return { ...prev, [field]: value };
      } else if (field in prev.address) {
        return { ...prev, address: { ...prev.address, [field]: value } };
      }
      return prev;
    });
  };

  const handleJointExtraChange = (field, value) => {
    setJoint(prev => ({ ...prev, [field]: value }));
  };

  const handleNumberOfCounterpartiesChange = (value) => {
    const num = parseInt(value);
    setNumberOfCounterparties(num);
    // Initialize or adjust counterparty forms array
    const newForms = [];
    for (let i = 0; i < num; i++) {
      newForms.push(jointCounterparties[i] || { ...jointCounterpartyFormTemplate });
    }
    setJointCounterparties(newForms);
  };

  const handleJointCounterpartyChange = (index, field, value) => {
    const updated = [...jointCounterparties];
    if (field in updated[index]) {
      updated[index] = { ...updated[index], [field]: value };
    } else if (field in updated[index].address) {
      updated[index] = {
        ...updated[index],
        address: { ...updated[index].address, [field]: value }
      };
    } else {
      // Handle cds_account and custodian_bank
      updated[index] = { ...updated[index], [field]: value };
    }
    setJointCounterparties(updated);
  };

  const handleCorporateChange = (field, value) => {
    setCorporate(prev => {
      if (field in prev) {
        return { ...prev, [field]: value };
      } else if (field in prev.address) {
        return { ...prev, address: { ...prev.address, [field]: value } };
      } else if (field in prev.banking) {
        return { ...prev, banking: { ...prev.banking, [field]: value } };
      }
      return prev;
    });
  };

  const handleCorporateExtraChange = (field, value) => {
    setCorporate(prev => ({ ...prev, [field]: value }));
  };

  async function handleJointSubmit(e) {
    e.preventDefault();
    
    // Validate counterparty forms
    for (let i = 0; i < numberOfCounterparties; i++) {
      const cp = jointCounterparties[i];
      if (!cp) {
        alert(`Please fill in details for Counterparty ${i + 1}`);
        return;
      }
      if (!cp.shortName || !cp.shortName.trim()) {
        alert(`Short Name is required for Counterparty ${i + 1}`);
        return;
      }
      if (!cp.idType || !cp.idType.trim()) {
        alert(`ID Type is required for Counterparty ${i + 1}`);
        return;
      }
      if (!cp.idNumber || !cp.idNumber.trim()) {
        alert(`ID Number is required for Counterparty ${i + 1}`);
        return;
      }
      if (!cp.longName || !cp.longName.trim()) {
        alert(`Long Name is required for Counterparty ${i + 1}`);
        return;
      }
    }
    
    try {
      const method = editMode ? 'PUT' : 'POST';
      const url = editMode 
        ? `${API_BASE_URL}/counterparty-joint/${editingId}`
        : `${API_BASE_URL}/counterparty-joint`;
      
      // Prepare counterparty data
      const counterpartyData = jointCounterparties.map((cp, index) => ({
        title: cp.title,
        short_name: cp.shortName,
        long_name: cp.longName,
        id_type: cp.idType,
        id_number: cp.idNumber,
        address: cp.address,
        cds_account: cp.cds_account || '',
        custodian_bank: cp.custodian_bank || '',
        sequence: index + 1
      }));
      
      // Generate joint short_name and long_name from individual counterparties
      const jointShortName = jointCounterparties.map(cp => cp.shortName).join(' & ');
      const jointLongName = jointCounterparties.map(cp => cp.longName).join(' & ');
      
      const response = await fetch(url, {
        method: method,
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title: jointCounterparties[0]?.title || 'Mr',
          short_name: jointShortName,
          long_name: jointLongName,
          id_type: jointCounterparties[0]?.idType || 'NIC',
          house_number: '',
          street_name: '',
          province: '',
          postal_code: '',
          city: '',
          country: '',
          telephone: '',
          email: '',
          mobile: '',
          cds_account: '',
          custodian_bank: '',
          counterparties: counterpartyData
        })
      });
      if (!response.ok) throw new Error('Failed to save data');
      const result = await response.json();
      setSavedCuxNumber(result.cux_number || savedCuxNumber);
      setShowJointToast(true);
      // Clear form after save (both create and edit)
      setJoint({ ...jointTemplate });
      setSavedCuxNumber(null);
      setEditMode(false);
      setEditingId(null);
      setNumberOfCounterparties(1);
      setJointCounterparties([{ ...jointCounterpartyFormTemplate }]);
      setTimeout(() => {
        setShowJointToast(false);
        setSavedCuxNumber(null);
      }, 5000);
    } catch (err) {
      alert('Error: ' + err.message);
    }
  }

  async function handleCorporateSubmit(e) {
    e.preventDefault();
    
    // Validation for mandatory fields
    if (!corporate.shortName || !corporate.shortName.trim()) {
      alert('Short Name is required');
      return;
    }
    if (!corporate.longName || !corporate.longName.trim()) {
      alert('Long Name is required');
      return;
    }
    
    try {
      const method = editMode ? 'PUT' : 'POST';
      const url = editMode 
        ? `${API_BASE_URL}/counterparty-corporate/${editingId}`
        : `${API_BASE_URL}/counterparty-corporate`;
      
      const response = await fetch(url, {
        method: method,
        headers: getAuthHeaders(),
        body: JSON.stringify({
          company_name: corporate.companyName,
          short_name: corporate.shortName,
          long_name: corporate.longName,
          registration_number: corporate.registrationNumber,
          tin_number: corporate.tinNumber,
          vat_number: corporate.vatNumber,
          address_line1: corporate.address.addressLine1,
          address_line2: corporate.address.addressLine2,
          city: corporate.address.city,
          state: corporate.address.state,
          country: corporate.address.country,
          postal_code: corporate.address.postalCode,
          phone_number: corporate.address.phoneNumber,
          email: corporate.address.email,
          website: corporate.address.website,
          kyc_status: corporate.kycStatus,
          risk_category: corporate.riskCategory,
          sanctions_check: corporate.sanctionsCheck,
          credit_limit: corporate.creditLimit,
          primary_bank_name: corporate.banking.primaryBankName,
          bank_account_number: corporate.banking.bankAccountNumber,
          swift_bic_code: corporate.banking.swiftBicCode,
          treasury_contact_person: corporate.banking.treasuryContactPerson,
          treasury_contact_email: corporate.banking.treasuryContactEmail,
          treasury_contact_phone: corporate.banking.treasuryContactPhone,
          custodian_bank: corporate.custodian_bank,
          cds_account: corporate.cds_account
        })
      });
      if (!response.ok) throw new Error('Failed to save data');
      const result = await response.json();
      setSavedCuxNumber(result.cux_number || savedCuxNumber);
      setShowCorporateToast(true);
      // Clear form after save (both create and edit)
      setCorporate({ ...corporateTemplate });
      setSavedCuxNumber(null);
      setEditMode(false);
      setEditingId(null);
      setTimeout(() => {
        setShowCorporateToast(false);
        setSavedCuxNumber(null);
      }, 5000);
    } catch (err) {
      alert('Error: ' + err.message);
    }
  }

  const renderIndividual = () => {
    const isReadOnly = viewMode;
    const submitText = editMode ? 'Update' : 'Submit';
    
    return (
    <form className="individual-section" onSubmit={handleSubmit}>
      {showIndividualToast && (
        <div style={{ background: '#4caf50', color: '#fff', padding: '10px', borderRadius: '5px', marginBottom: 10, textAlign: 'center', fontWeight: 'bold' }}>
            {editMode ? 'Counterparty Individual updated successfully!' : 'Counterparty Individual saved successfully!'}
            {savedCuxNumber && (
              <div style={{ marginTop: '8px', fontSize: '14px' }}>
                CUX Number: <strong>{savedCuxNumber}</strong>
              </div>
            )}
        </div>
      )}
      {validationError && !validationError.includes('NIC number') && (
        <div style={{ 
          background: '#ffebee', 
          color: '#d32f2f', 
          padding: '12px', 
          borderRadius: '5px', 
          marginBottom: 10, 
          border: '1px solid #f5c6cb',
          fontWeight: '500'
        }}>
          {validationError}
        </div>
      )}
      <div className="individual-block">
          <div className="row">
            <label style={{ width: '100%', marginBottom: '10px' }}>
              CUX Number
              <input 
                type="text" 
                value={savedCuxNumber || 'Will be generated on save'} 
                readOnly 
                style={{ 
                  background: '#f5f5f5', 
                  cursor: 'not-allowed',
                  fontStyle: savedCuxNumber ? 'normal' : 'italic',
                  color: savedCuxNumber ? '#000' : '#999'
                }}
              />
              <small style={{ display: 'block', color: '#666', marginTop: '4px' }}>
                System-generated counterparty identification number
              </small>
            </label>
          </div>
        <div className="row">
          <label>Title
              <select 
                value={individual.title} 
                onChange={e => handleIndividualChange('title', e.target.value)}
                disabled={isReadOnly}
                style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
              >
              {titles.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
            <label>Short Name *
              <input 
                type="text" 
                value={individual.shortName} 
                onChange={e => handleIndividualChange('shortName', e.target.value)}
                readOnly={isReadOnly}
                required
                style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
              />
          </label>
            <label>ID Type *
              <select 
                value={individual.idType} 
                onChange={e => handleIndividualChange('idType', e.target.value)}
                disabled={isReadOnly}
                required
                style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
              >
              <option value="">Select</option>
              {idTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
            {individual.idType && (
              <label>
                {individual.idType === 'NIC' ? 'NIC Number' : 
                 individual.idType === 'Passport' ? 'Passport Number' :
                 individual.idType === 'Driving License' ? 'Driving License Number' :
                 individual.idType === 'Business Registration' ? 'Business Registration Number' :
                 'ID Number'}
                <input 
                  type="text" 
                  value={individual.idNumber} 
                  onChange={e => handleIndividualChange('idNumber', e.target.value)}
                  placeholder={`Enter ${individual.idType} number`}
                  readOnly={isReadOnly}
                  style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                  className={validationError && validationError.includes('NIC number') ? 'error-input' : ''}
                />
                {validationError && validationError.includes('NIC number') && (
                  <span style={{ 
                    color: '#d32f2f', 
                    fontSize: '0.875rem', 
                    display: 'block', 
                    marginTop: '4px',
                    fontWeight: '500'
                  }}>
                    {validationError}
                  </span>
                )}
              </label>
            )}
        </div>
        <div className="row">
            <label>Long Name *
              <input 
                type="text" 
                value={individual.longName} 
                onChange={e => handleIndividualChange('longName', e.target.value)}
                readOnly={isReadOnly}
                required
                style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
              />
          </label>
        </div>
        <div className="address-section">
          <div className="row">
            <label>House Number
              <input 
                type="text" 
                value={individual.address.houseNumber} 
                onChange={e => handleIndividualChange('houseNumber', e.target.value)}
                readOnly={isReadOnly}
                style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
              />
            </label>
            <label>Street Name
              <input 
                type="text" 
                value={individual.address.streetName} 
                onChange={e => handleIndividualChange('streetName', e.target.value)}
                readOnly={isReadOnly}
                style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
              />
            </label>
          </div>
          <div className="row">
            <label>Province
              <input 
                type="text" 
                value={individual.address.province} 
                onChange={e => handleIndividualChange('province', e.target.value)}
                readOnly={isReadOnly}
                style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
              />
            </label>
            <label>Postal Code
              <input 
                type="text" 
                value={individual.address.postalCode} 
                onChange={e => handleIndividualChange('postalCode', e.target.value)}
                readOnly={isReadOnly}
                style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
              />
            </label>
            <label>City
              <input 
                type="text" 
                value={individual.address.city} 
                onChange={e => handleIndividualChange('city', e.target.value)}
                readOnly={isReadOnly}
                style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
              />
            </label>
          </div>
          <div className="row">
            <label>Country
              <input 
                type="text" 
                value={individual.address.country} 
                onChange={e => handleIndividualChange('country', e.target.value)}
                readOnly={isReadOnly}
                style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
              />
            </label>
            <label>CDS Account
              <input 
                type="text" 
                value={individual.cds_account} 
                onChange={e => handleIndividualExtraChange('cds_account', e.target.value)} 
                placeholder="Enter CDS Account"
                readOnly={isReadOnly}
                style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
              />
            </label>
            <label>Custodian Bank
              <select 
                value={individual.custodian_bank} 
                onChange={e => handleIndividualExtraChange('custodian_bank', e.target.value)}
                disabled={isReadOnly}
                style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
              >
                <option value="">Select Custodian Bank</option>
                {CUSTODIAN_BANKS.map(bank => (
                  <option key={bank} value={bank}>{bank}</option>
                ))}
              </select>
            </label>
            <label>Email
              <input 
                type="email" 
                value={individual.address.email} 
                onChange={e => handleIndividualChange('email', e.target.value)}
                readOnly={isReadOnly}
                style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
              />
            </label>
            <label>Telephone
              <input 
                type="text" 
                value={individual.address.telephone} 
                onChange={e => handleIndividualChange('telephone', e.target.value)}
                readOnly={isReadOnly}
                style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
              />
            </label>
            <label>Mobile
              <input 
                type="text" 
                value={individual.address.mobile} 
                onChange={e => handleIndividualChange('mobile', e.target.value)}
                readOnly={isReadOnly}
                style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
              />
            </label>
          </div>
        </div>
      </div>
      {!isReadOnly && (
        <button type="submit" className="submit-btn">{submitText}</button>
      )}
    </form>
  );
  };

  const renderCorporate = () => {
    const isReadOnly = viewMode;
    const submitText = editMode ? 'Update' : 'Submit';
    
    return (
    <form className="corporate-section" onSubmit={handleCorporateSubmit} style={{ background: '#f0f8ff', padding: 20, borderRadius: 8, marginTop: 24 }}>
      {showCorporateToast && (
        <div style={{ background: '#4caf50', color: '#fff', padding: '10px', borderRadius: '5px', marginBottom: 10, textAlign: 'center', fontWeight: 'bold' }}>
            {editMode ? 'Corporate Counterparty updated successfully!' : 'Corporate Counterparty saved successfully!'}
            {savedCuxNumber && (
              <div style={{ marginTop: '8px', fontSize: '14px' }}>
                CUX Number: <strong>{savedCuxNumber}</strong>
              </div>
            )}
        </div>
      )}
      <div className="corporate-block">
        <h3>Customer Code Section</h3>
        <div className="row">
          <label style={{ width: '100%', marginBottom: '10px' }}>
            CUX Number
            <input 
              type="text" 
              value={savedCuxNumber || 'Will be generated on save'} 
              readOnly 
              style={{ 
                background: '#f5f5f5', 
                cursor: 'not-allowed',
                fontStyle: savedCuxNumber ? 'normal' : 'italic',
                color: savedCuxNumber ? '#000' : '#999'
              }}
            />
            <small style={{ display: 'block', color: '#666', marginTop: '4px' }}>
              System-generated counterparty identification number
            </small>
          </label>
        </div>
        <div className="row">
          <label>Company Name
            <input type="text" value={corporate.companyName} onChange={e => handleCorporateChange('companyName', e.target.value)} required />
          </label>
          <label>Short Name *
            <input 
              type="text" 
              value={corporate.shortName} 
              onChange={e => handleCorporateChange('shortName', e.target.value)}
              required
            />
          </label>
          <label>Registration Number
            <input type="text" value={corporate.registrationNumber} onChange={e => handleCorporateChange('registrationNumber', e.target.value)} />
          </label>
        </div>
        <div className="row">
          <label>Long Name *
            <input 
              type="text" 
              value={corporate.longName} 
              onChange={e => handleCorporateChange('longName', e.target.value)}
              required
            />
          </label>
          <label>TIN Number
            <input type="text" value={corporate.tinNumber} onChange={e => handleCorporateChange('tinNumber', e.target.value)} />
          </label>
          <label>VAT Number
            <input type="text" value={corporate.vatNumber} onChange={e => handleCorporateChange('vatNumber', e.target.value)} />
          </label>
        </div>

        <h3>Contact Information</h3>
        <div className="address-section">
          <div className="row">
            <label>Address Line 1
              <input type="text" value={corporate.address.addressLine1} onChange={e => handleCorporateChange('addressLine1', e.target.value)} />
            </label>
            <label>Address Line 2
              <input type="text" value={corporate.address.addressLine2} onChange={e => handleCorporateChange('addressLine2', e.target.value)} />
            </label>
          </div>
          <div className="row">
            <label>City
              <input type="text" value={corporate.address.city} onChange={e => handleCorporateChange('city', e.target.value)} />
            </label>
            <label>State
              <input type="text" value={corporate.address.state} onChange={e => handleCorporateChange('state', e.target.value)} />
            </label>
            <label>Country
              <input type="text" value={corporate.address.country} onChange={e => handleCorporateChange('country', e.target.value)} />
            </label>
          </div>
          <div className="row">
            <label>Postal Code
              <input type="text" value={corporate.address.postalCode} onChange={e => handleCorporateChange('postalCode', e.target.value)} />
            </label>
            <label>Phone Number
              <input type="text" value={corporate.address.phoneNumber} onChange={e => handleCorporateChange('phoneNumber', e.target.value)} />
            </label>
            <label>Email
              <input type="email" value={corporate.address.email} onChange={e => handleCorporateChange('email', e.target.value)} />
            </label>
            <label>Website
              <input type="url" value={corporate.address.website} onChange={e => handleCorporateChange('website', e.target.value)} />
            </label>
          </div>
        </div>

        <h3>Compliance & Risk</h3>
        <div className="row">
          <label>KYC Status
            <select value={corporate.kycStatus} onChange={e => handleCorporateChange('kycStatus', e.target.value)}>
              {kycStatuses.map(status => <option key={status} value={status}>{status}</option>)}
            </select>
          </label>
          <label>Risk Category
            <select value={corporate.riskCategory} onChange={e => handleCorporateChange('riskCategory', e.target.value)}>
              {riskCategories.map(category => <option key={category} value={category}>{category}</option>)}
            </select>
          </label>
          <label>Sanctions Check
            <select value={corporate.sanctionsCheck} onChange={e => handleCorporateChange('sanctionsCheck', e.target.value)}>
              {sanctionsChecks.map(check => <option key={check} value={check}>{check}</option>)}
            </select>
          </label>
          <label>Credit Limit
            <input type="number" value={corporate.creditLimit} onChange={e => handleCorporateChange('creditLimit', e.target.value)} />
          </label>
        </div>

        <h3>Banking & Treasury Details</h3>
        <div className="row">
          <label>Primary Bank Name
            <input type="text" value={corporate.banking.primaryBankName} onChange={e => handleCorporateChange('primaryBankName', e.target.value)} />
          </label>
          <label>Bank Account Number
            <input type="text" value={corporate.banking.bankAccountNumber} onChange={e => handleCorporateChange('bankAccountNumber', e.target.value)} />
          </label>
          <label>Swift/BIC Code
            <input type="text" value={corporate.banking.swiftBicCode} onChange={e => handleCorporateChange('swiftBicCode', e.target.value)} />
          </label>
        </div>
        <div className="row">
          <label>Treasury Contact Person
            <input type="text" value={corporate.banking.treasuryContactPerson} onChange={e => handleCorporateChange('treasuryContactPerson', e.target.value)} />
          </label>
          <label>Treasury Contact Email
            <input type="email" value={corporate.banking.treasuryContactEmail} onChange={e => handleCorporateChange('treasuryContactEmail', e.target.value)} />
          </label>
          <label>Treasury Contact Phone
            <input type="text" value={corporate.banking.treasuryContactPhone} onChange={e => handleCorporateChange('treasuryContactPhone', e.target.value)} />
          </label>
        </div>

        <div className="row">
          <label>CDS Account
            <input type="text" value={corporate.cds_account} onChange={e => handleCorporateExtraChange('cds_account', e.target.value)} placeholder="Enter CDS Account" />
          </label>
          <label>Custodian Bank
            <select value={corporate.custodian_bank} onChange={e => handleCorporateExtraChange('custodian_bank', e.target.value)}>
              <option value="">Select Custodian Bank</option>
              {CUSTODIAN_BANKS.map(bank => (
                <option key={bank} value={bank}>{bank}</option>
              ))}
            </select>
          </label>
        </div>
      </div>
      {!isReadOnly && (
        <button type="submit" className="submit-btn">{submitText}</button>
      )}
    </form>
  );
  };

  const renderJoint = () => {
    const isReadOnly = viewMode;
    const submitText = editMode ? 'Update' : 'Submit';
    
    return (
    <form className="joint-section" onSubmit={handleJointSubmit} style={{ background: '#eaf6ff', padding: 20, borderRadius: 8, marginTop: 24 }}>
      {showJointToast && (
        <div style={{ background: '#4caf50', color: '#fff', padding: '10px', borderRadius: '5px', marginBottom: 10, textAlign: 'center', fontWeight: 'bold' }}>
            {editMode ? 'Joint Counterparty updated successfully!' : 'Joint Counterparty saved successfully!'}
            {savedCuxNumber && (
              <div style={{ marginTop: '8px', fontSize: '14px' }}>
                CUX Number: <strong>{savedCuxNumber}</strong>
              </div>
            )}
        </div>
      )}
      <div className="joint-block">
        <div className="row">
          <label style={{ width: '100%', marginBottom: '10px' }}>
            CUX Number
            <input 
              type="text" 
              value={savedCuxNumber || 'Will be generated on save'} 
              readOnly 
              style={{ 
                background: '#f5f5f5', 
                cursor: 'not-allowed',
                fontStyle: savedCuxNumber ? 'normal' : 'italic',
                color: savedCuxNumber ? '#000' : '#999'
              }}
            />
            <small style={{ display: 'block', color: '#666', marginTop: '4px' }}>
              System-generated counterparty identification number
            </small>
          </label>
        </div>
        {!isReadOnly && (
          <div className="row">
            <label style={{ width: '100%', marginBottom: '10px' }}>
              Number of Counterparties *
              <select 
                value={numberOfCounterparties} 
                onChange={e => handleNumberOfCounterpartiesChange(e.target.value)}
                required
                style={{ width: '100%' }}
              >
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
              </select>
              <small style={{ display: 'block', color: '#666', marginTop: '4px' }}>
                Select the number of counterparties in this joint account (1-4)
              </small>
            </label>
          </div>
        )}
        {isReadOnly && (
          <div className="row">
            <label style={{ width: '100%', marginBottom: '10px' }}>
              Number of Counterparties
              <input
                type="text"
                value={String(numberOfCounterparties || jointCounterparties.length || 0)}
                readOnly
                style={{ background: '#f5f5f5', cursor: 'not-allowed', width: '100%' }}
              />
            </label>
          </div>
        )}
        {numberOfCounterparties > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ marginBottom: '15px', color: '#333', padding: '10px', background: '#e3f2fd', borderRadius: '5px' }}>
              Counterparty Details
            </h4>
            {jointCounterparties.map((cp, index) => (
              <div key={index} style={{ marginBottom: '25px', padding: '20px', background: '#f9f9f9', borderRadius: '8px', border: '1px solid #ddd' }}>
                <h5 style={{ marginBottom: '15px', color: '#1a237e', borderBottom: '2px solid #1a237e', paddingBottom: '8px' }}>
                  Counterparty {index + 1}
                </h5>
                <div className="row">
                  <label>Title
                    <select 
                      value={cp.title} 
                      onChange={e => handleJointCounterpartyChange(index, 'title', e.target.value)}
                      disabled={isReadOnly}
                      style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                    >
                      {titles.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </label>
                  <label>Short Name *
                    <input 
                      type="text" 
                      value={cp.shortName} 
                      onChange={e => handleJointCounterpartyChange(index, 'shortName', e.target.value)}
                      readOnly={isReadOnly}
                      required
                      style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                    />
                  </label>
                  <label>ID Type *
                    <select 
                      value={cp.idType} 
                      onChange={e => handleJointCounterpartyChange(index, 'idType', e.target.value)}
                      disabled={isReadOnly}
                      required
                      style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select</option>
                      {idTypes.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </label>
                  {cp.idType && (
                    <label>
                      {cp.idType === 'NIC' ? 'NIC Number' : 
                       cp.idType === 'Passport' ? 'Passport Number' :
                       cp.idType === 'Driving License' ? 'Driving License Number' :
                       cp.idType === 'Business Registration' ? 'Business Registration Number' :
                       'ID Number'} *
                      <input 
                        type="text" 
                        value={cp.idNumber} 
                        onChange={e => handleJointCounterpartyChange(index, 'idNumber', e.target.value)}
                        placeholder={`Enter ${cp.idType} number`}
                        readOnly={isReadOnly}
                        required
                        style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                      />
                    </label>
                  )}
                </div>
                <div className="row">
                  <label>Long Name *
                    <input 
                      type="text" 
                      value={cp.longName} 
                      onChange={e => handleJointCounterpartyChange(index, 'longName', e.target.value)}
                      readOnly={isReadOnly}
                      required
                      style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                    />
                  </label>
                </div>
                <div className="address-section" style={{ marginTop: '15px' }}>
                  <h6 style={{ marginBottom: '10px', color: '#555' }}>Address</h6>
                  <div className="row">
                    <label>House Number
                      <input 
                        type="text" 
                        value={cp.address.houseNumber} 
                        onChange={e => handleJointCounterpartyChange(index, 'houseNumber', e.target.value)}
                        readOnly={isReadOnly}
                        style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                      />
                    </label>
                    <label>Street Name
                      <input 
                        type="text" 
                        value={cp.address.streetName} 
                        onChange={e => handleJointCounterpartyChange(index, 'streetName', e.target.value)}
                        readOnly={isReadOnly}
                        style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                      />
                    </label>
                  </div>
                  <div className="row">
                    <label>Province
                      <input 
                        type="text" 
                        value={cp.address.province} 
                        onChange={e => handleJointCounterpartyChange(index, 'province', e.target.value)}
                        readOnly={isReadOnly}
                        style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                      />
                    </label>
                    <label>Postal Code
                      <input 
                        type="text" 
                        value={cp.address.postalCode} 
                        onChange={e => handleJointCounterpartyChange(index, 'postalCode', e.target.value)}
                        readOnly={isReadOnly}
                        style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                      />
                    </label>
                    <label>City
                      <input 
                        type="text" 
                        value={cp.address.city} 
                        onChange={e => handleJointCounterpartyChange(index, 'city', e.target.value)}
                        readOnly={isReadOnly}
                        style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                      />
                    </label>
                  </div>
                  <div className="row">
                    <label>Country
                      <input 
                        type="text" 
                        value={cp.address.country} 
                        onChange={e => handleJointCounterpartyChange(index, 'country', e.target.value)}
                        readOnly={isReadOnly}
                        style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                      />
                    </label>
                    <label>Email
                      <input 
                        type="email" 
                        value={cp.address.email} 
                        onChange={e => handleJointCounterpartyChange(index, 'email', e.target.value)}
                        readOnly={isReadOnly}
                        style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                      />
                    </label>
                    <label>Telephone
                      <input 
                        type="text" 
                        value={cp.address.telephone} 
                        onChange={e => handleJointCounterpartyChange(index, 'telephone', e.target.value)}
                        readOnly={isReadOnly}
                        style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                      />
                    </label>
                    <label>Mobile
                      <input 
                        type="text" 
                        value={cp.address.mobile} 
                        onChange={e => handleJointCounterpartyChange(index, 'mobile', e.target.value)}
                        readOnly={isReadOnly}
                        style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                      />
                    </label>
                  </div>
                  <div className="row">
                    <label>CDS Account
                      <input 
                        type="text" 
                        value={cp.cds_account || ''} 
                        onChange={e => handleJointCounterpartyChange(index, 'cds_account', e.target.value)}
                        placeholder="Enter CDS Account"
                        readOnly={isReadOnly}
                        style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                      />
                    </label>
                    <label>Custodian Bank
                      <select 
                        value={cp.custodian_bank || ''} 
                        onChange={e => handleJointCounterpartyChange(index, 'custodian_bank', e.target.value)}
                        disabled={isReadOnly}
                        style={isReadOnly ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                      >
                        <option value="">Select Custodian Bank</option>
                        {CUSTODIAN_BANKS.map(bank => (
                          <option key={bank} value={bank}>{bank}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {!isReadOnly && (
        <button type="submit" className="submit-btn">{submitText}</button>
      )}
    </form>
  );
  };

  // Submit handler for Individual
  async function handleSubmit(e) {
    e.preventDefault();
    
    // Clear previous validation errors
    setValidationError('');
    
    // Validation for mandatory fields
    if (!individual.shortName || !individual.shortName.trim()) {
      setValidationError('Short Name is required');
      return;
    }
    if (!individual.idType || !individual.idType.trim()) {
      setValidationError('ID Type is required');
      return;
    }
    if (!individual.longName || !individual.longName.trim()) {
      setValidationError('Long Name is required');
      return;
    }
    
    try {
      const method = editMode ? 'PUT' : 'POST';
      const url = editMode 
        ? `${API_BASE_URL}/counterparty-individual/${editingId}`
        : `${API_BASE_URL}/counterparty-individual`;
      
      const response = await fetch(url, {
        method: method,
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title: individual.title,
          short_name: individual.shortName,
          long_name: individual.longName,
          id_type: individual.idType,
          id_number: individual.idNumber,
          house_number: individual.address.houseNumber,
          street_name: individual.address.streetName,
          province: individual.address.province,
          postal_code: individual.address.postalCode,
          city: individual.address.city,
          country: individual.address.country,
          telephone: individual.address.telephone,
          email: individual.address.email,
          mobile: individual.address.mobile,
          cds_account: individual.cds_account,
          custodian_bank: individual.custodian_bank
        })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        // Check if it's a NIC duplicate error
        const errorMessage = result.error || 'Failed to save data';
        setValidationError(errorMessage);
        return;
      }
      
      setSavedCuxNumber(result.cux_number || savedCuxNumber);
      setShowIndividualToast(true);
      setValidationError(''); // Clear any errors on success
      // Clear form after save (both create and edit)
      setIndividual({ ...individualTemplate });
      setSavedCuxNumber(null);
      setEditMode(false);
      setEditingId(null);
      setTimeout(() => {
        setShowIndividualToast(false);
        setSavedCuxNumber(null);
      }, 5000);
    } catch (err) {
      setValidationError(err.message || 'An error occurred while saving');
    }
  }

  // Render list view
  const renderListView = () => {
    const currentList = counterparties[type.toLowerCase()] || [];
    
    return (
      <div style={{ marginTop: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h3>{type} Counterparties ({currentList.length})</h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              disabled={loading}
              onClick={() => exportCounterpartiesToExcel(counterparties)}
              title="Exports all categories (Individual, Joint, Corporate), one sheet each, with addresses"
              style={{ padding: '8px 16px', background: '#217346', color: 'white', border: 'none', borderRadius: '4px', cursor: loading ? 'not-allowed' : 'pointer' }}
            >
              Export to Excel
            </button>
            <button
              type="button"
              onClick={() => setShowList(false)}
              style={{ padding: '8px 16px', background: '#2196F3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              New {type}
            </button>
          </div>
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>
        ) : currentList.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>No {type.toLowerCase()} counterparties found.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>CUX Number</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Short Name</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Long Name</th>
                {type === 'Individual' && <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>ID Number</th>}
                {type === 'Corporate' && <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Company Name</th>}
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Email</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentList.map((cp) => (
                <tr key={cp.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '12px' }}>{cp.cux_number || 'N/A'}</td>
                  <td style={{ padding: '12px' }}>{cp.short_name || 'N/A'}</td>
                  <td style={{ padding: '12px' }}>{cp.long_name || 'N/A'}</td>
                  {type === 'Individual' && <td style={{ padding: '12px' }}>{cp.id_number || 'N/A'}</td>}
                  {type === 'Corporate' && <td style={{ padding: '12px' }}>{cp.company_name || 'N/A'}</td>}
                  <td style={{ padding: '12px' }}>{cp.email || 'N/A'}</td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleView(cp.id)}
                      style={{ 
                        padding: '6px 12px', 
                        margin: '0 4px', 
                        background: '#4CAF50', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px', 
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleEdit(cp.id)}
                      style={{ 
                        padding: '6px 12px', 
                        margin: '0 4px', 
                        background: '#2196F3', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px', 
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  return (
    <div className="page-container ui-page" style={{ padding: '20px', background: 'var(--bg-page)' }}>
      <div className="counterparty-master-page ui-card">
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <button 
              type="button" 
              onClick={() => { setShowList(!showList); handleReset(); }}
              style={{ 
                padding: '10px 20px', 
                marginLeft: '10px',
                background: showList ? '#f44336' : '#4CAF50', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px', 
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {showList ? 'Hide List' : 'View All Counterparties'}
            </button>
          </div>
        </div>
      
      {showList ? (
        <>
          <div className="type-selector" style={{ marginBottom: '20px' }}>
            <label>Type
              <select value={type} onChange={handleTypeChange}>
                <option value="Individual">Individual</option>
                <option value="Joint">Joint</option>
                <option value="Corporate">Corporate</option>
              </select>
            </label>
          </div>
          {renderListView()}
        </>
      ) : (
        <>
          {(viewMode || editMode) && (
            <div style={{ marginBottom: '15px', display: 'flex', gap: '10px' }}>
              <button 
                type="button" 
                onClick={handleReset}
                style={{ 
                  padding: '8px 16px', 
                  background: '#f44336', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px', 
                  cursor: 'pointer' 
                }}
              >
                {viewMode ? 'Close View' : 'Cancel Edit'}
              </button>
              {viewMode && (
                <button 
                  type="button" 
                  onClick={() => { setViewMode(false); setEditMode(true); }}
                  style={{ 
                    padding: '8px 16px', 
                    background: '#2196F3', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px', 
                    cursor: 'pointer' 
                  }}
                >
                  Edit
                </button>
              )}
        </div>
      )}
      <div className="type-selector">
        <label>Type
              <select value={type} onChange={handleTypeChange} disabled={viewMode || editMode}>
            <option value="Individual">Individual</option>
            <option value="Joint">Joint</option>
            <option value="Corporate">Corporate</option>
          </select>
        </label>
      </div>
          {type === 'Individual' ? renderIndividual() : type === 'Joint' ? renderJoint() : renderCorporate()}
        </>
      )}
      </div>
    </div>
  );
}
